// « Mon accès Claude (MCP) » (user menu and profile of a member, when the server reports
// features.mcpPersonal): his own personal keys of the office's MCP connector (GET
// /api/me/mcp-keys → {keys, maxActive}), made for him by an administrator (Administration → Accès
// Claude (MCP) → « Accès personnel pour… »), with « Révoquer » (confirmed, DELETE
// /api/me/mcp-keys/:id). No key is made here: the administrator creates it and hands the address
// over. Also what a key gives (his rights and his « Connecteurs autorisés », never more) and how to
// add the connector on claude.ai (the steps of the address window, « Aucune connexion »). The list
// never holds a secret: name, hint, dates.
import { h, badge, button, emptyState, spinner, icon } from '../dom.js';
import { t } from '../i18n/fr.js';
import { relativeTime, formatDateTime } from '../format.js';
import { normalizeKeys, connectSteps, REVOKE_REASONS } from './admin/mcp.js';
import { grantsSummary, errorText } from './admin/model.js';
import { policySummary } from './connectorpolicy.js';
import './mcpaccess/strings.js';

const enc = encodeURIComponent;

/**
 * Opens the dialog.
 * @param {object} ctx app context
 * @returns {import('./overlay.js').Layer}
 */
export function openMyMcpAccess(ctx) {
  const state = ctx.store.get();
  const me = state.me || {};
  let keys = [];
  let closed = false;
  const revoking = new Set();

  const list = h('div', { class: 'session-list mcpa-list', 'aria-live': 'polite' }, spinner(t('mcpa.loading')));
  const rights = h('p', { class: 'adm-note mcpa-rights' }, t('mcpa.rights', {
    grants: grantsSummary(me, state.office || {}), conns: policySummary(me.connectorPolicy, state.connectors || []),
  }));
  const content = h('div', { class: 'stack mcpa' },
    h('p', { class: 'mcpa-intro' }, t('mcpa.intro')),
    rights,
    h('h3', { class: 'section-title mono-label' }, t('mcpa.keys')),
    list,
    h('p', { class: 'adm-note mcpa-howget' }, t('mcpa.howGet')),
    connectSteps({ title: t('mcpa.steps') }),
    h('p', { class: 'adm-alert is-amber', role: 'note' }, icon('shield', { size: 16 }), h('span', {}, t('mcpa.secretWarn'))));
  const layer = ctx.overlay.openModal({ title: t('mcpa.title'), subtitle: t('mcpa.subtitle'), size: 'md', content, className: 'mcpa-modal' });
  layer.onClose?.(() => { closed = true; });

  async function load() {
    try {
      const res = await ctx.api.get('/api/me/mcp-keys');
      if (closed) return;
      keys = normalizeKeys(res).keys.filter((k) => k.kind === 'personal');
      render();
    } catch (err) {
      if (closed) return;
      list.replaceChildren(err?.status === 404
        ? emptyState({ icon: 'info', title: t('mcpa.unavailable') })
        : emptyState({
          icon: 'alert', title: t('mcpa.loadError'), text: err?.message || t('common.error'),
          action: button(t('mcpa.retry'), { variant: 'secondary', icon: 'refresh', onClick: () => { list.replaceChildren(spinner(t('mcpa.loading'))); load(); } }),
        }));
    }
  }

  function render() {
    if (!keys.length) {
      list.replaceChildren(emptyState({ icon: 'key', title: t('mcpa.empty') }));
      return;
    }
    const now = Date.now();
    list.replaceChildren(...keys.map((k) => keyRow(k, now)));
  }

  function keyRow(k, now) {
    const created = k.createdAt ? relativeTime(k.createdAt, now) : '';
    const meta = [
      t('mcpa.hint', { hint: k.hint || '????' }),
      created ? (k.createdByName ? t('mcpa.created', { when: created, name: k.createdByName }) : t('mcpa.createdNoName', { when: created })) : '',
      k.lastUsedAt ? t('mcpa.lastUsed', { when: relativeTime(k.lastUsedAt, now) }) : t('mcpa.neverUsed'),
    ].filter(Boolean).join(' · ');
    const reason = REVOKE_REASONS.includes(k.revokedReason) ? t(`adm.mcp.reason.${k.revokedReason}`) : '';
    const busy = revoking.has(k.id);
    const action = k.revoked
      ? badge(t('mcpa.revokedBadge'), 'neutral', {
        title: k.revokedAt ? (reason ? t('adm.mcp.revokedWhy', { when: formatDateTime(k.revokedAt), reason }) : t('adm.mcp.revokedOn', { when: formatDateTime(k.revokedAt) })) : undefined,
      })
      : button(t('mcpa.revoke'), {
        variant: 'ghost', size: 'sm', class: 'mcpa-revoke', ariaLabel: t('mcpa.revokeAria', { name: k.name }), disabled: busy, busy,
        onClick: () => revoke(k),
      });
    return h('div', { class: ['session-row', 'mcpa-row', { 'is-revoked': k.revoked }], dataset: { id: k.id } },
      h('span', { class: 'session-icon' }, icon('key', { size: 18 })),
      h('div', { class: 'session-main' },
        h('p', { class: 'session-device mcpa-name' }, k.name || '—'),
        h('p', { class: 'session-meta' }, meta)),
      action);
  }

  async function revoke(k) {
    if (revoking.has(k.id) || k.revoked) return;
    const ok = await ctx.overlay.confirm({
      title: t('mcpa.revokeTitle', { name: k.name }), message: t('mcpa.revokeText'), confirmLabel: t('mcpa.revokeOk'), danger: true,
    });
    if (!ok || closed) return;
    revoking.add(k.id);
    render();
    try {
      await ctx.api.del(`/api/me/mcp-keys/${enc(k.id)}`);
      ctx.toast.success(t('mcpa.revoked', { name: k.name }));
    } catch (err) {
      ctx.toast.error(errorText(err));
    } finally {
      revoking.delete(k.id);
      if (!closed) load();
    }
  }

  load();
  return layer;
}
